import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'rsuite';
import { map, has, split } from 'lodash/fp';
import { useDispatch, useSelector } from 'react-redux';
import DateCell from '../../widgets/custom-cell/Date';
import PathKeyCell from '../../widgets/custom-cell/PathKey';
import CustomHeaderCell from '../../widgets/custom-cell/HeaderCell';
import AutoHeightTable from '../../AutoHeightTable';
import Pagination from '../../Pagination';
import { SettingsByField } from '../../../constants/visit/AssocVisitFields';
import {
  getPaginationLoadedPages,
  getPaginationWithoutPages
} from '../../../redux/selectors/report/visit-history/pagination';
import {
  resetVisitHistory,
  visitsHistoryOnChangePage,
  visitsHistoryOnChangeOrder,
  visitsHistoryOnChangeLimit
} from '../../../redux/actions/report/visit-history';
import { fetchVisitHistory } from '../../../redux/sagas/routines';
import orderList from '../../../helpers/orderList';

const { Column } = Table;

const VisitHistoryList = ({ data, queryOptions, isFetching }) => {
  const dispatch = useDispatch();
  const { order, columns } = queryOptions;
  const pagination = useSelector(getPaginationWithoutPages);
  const loadedPages = useSelector(getPaginationLoadedPages);

  const [sortColumn, sortType] = useMemo(() => split(':', order), [order]);

  const orderedData = useMemo(() => orderList(order, data), [order, data]);

  const handleSortColumn = (column, type) => {
    const newOrder = `${column}:${type}`;
    dispatch(visitsHistoryOnChangeOrder(newOrder));
    dispatch(fetchVisitHistory({ ...queryOptions, order: newOrder }));
  };

  const handleChangePage = page => {
    const isPageLoaded = has(page, loadedPages);
    dispatch(visitsHistoryOnChangePage(page));
    if (!isPageLoaded) {
      dispatch(fetchVisitHistory({ ...queryOptions, page }));
    }
  };

  const handleChangeLimit = limit => {
    dispatch(visitsHistoryOnChangeLimit(limit));
    dispatch(resetVisitHistory());
    dispatch(fetchVisitHistory({ ...queryOptions, limit, page: 1 }));
  };

  return (
    <Fragment>
      <AutoHeightTable
        bordered
        reduceHeight={540}
        data={orderedData}
        loading={isFetching}
        onSortColumn={handleSortColumn}
        sortColumn={sortColumn}
        sortType={sortType}
      >
        <Column width={100} sortable>
          <CustomHeaderCell>Source</CustomHeaderCell>
          <PathKeyCell dataKey="source" />
        </Column>

        <Column width={120} sortable>
          <CustomHeaderCell>Visit On</CustomHeaderCell>
          <DateCell dataKey="visit_on" />
        </Column>

        {map(column => {
          const [, dataKey] = column.split('.');
          return (
            <Column key={dataKey} width={120} resizable sortable>
              <CustomHeaderCell>
                {has(column, SettingsByField)
                  ? SettingsByField[column].label
                  : dataKey}
              </CustomHeaderCell>
              <PathKeyCell dataKey={dataKey} />
            </Column>
          );
        }, columns)}
      </AutoHeightTable>
      {pagination && (
        <Pagination
          page={pagination.currentPage}
          limit={pagination.limit}
          total={pagination.totalCount}
          onChangePage={handleChangePage}
          onChangeLimit={handleChangeLimit}
        />
      )}
    </Fragment>
  );
};

VisitHistoryList.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  queryOptions: PropTypes.shape({
    filter: PropTypes.shape({}).isRequired,
    columns: PropTypes.arrayOf(PropTypes.string).isRequired,
    includeAssocVisits: PropTypes.number.isRequired,
    order: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
  }).isRequired,
  isFetching: PropTypes.bool.isRequired
};

export default VisitHistoryList;
